import { useApolloClient } from '@apollo/client';
import { useI18n } from '@i18n';
import { OngoingExportsSubscription } from '@predium/client-graphql';
import { async_job_status_type_enum, export_jobs_enum } from '@predium/enums';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useSnackbar } from 'notistack';
import { createContext, PropsWithChildren, RefObject, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useSession } from '@/components/session/useSession';
import { captureClientException } from '@/utils/posthogErrorTracking';
import {
  COMPLETED_BATCH_EXPORTS,
  GET_BATCH_ESG_ANALYSIS_PDF_EXPORT_DOWNLOAD_URL,
  GET_FILE_DOWNLOAD_URL,
  GET_FILE_ID_BY_ASYNC_JOB_ID,
  ONGOING_EXPORTS_SUBSCRIPTION,
  USER_FILES_LIST,
} from '../../graphql/Downloads.queries';
import useSubscriptionFallback from '../../hooks/useSubscriptionFallback';
import { downloadFileUri, getAttachmentFilenameFromUrl } from '../../utils/createDownloadLink';
import { SnackbarTimeouts } from '../NotistackProvider';

type DownloadsSeenState = {
  pendingJobIds: number[];
};

type DownloadsContextType = {
  // State
  ongoingExports: OngoingExportsSubscription['async_job'];

  // Actions
  downloadFile: (fileId: number) => void;
  downloadBatchExport: (asyncJobId: number) => void;
  trackAsyncJob: (jobId: number) => void;

  // Popover state
  isPopoverOpen: boolean;
  popoverAnchorEl: RefObject<HTMLButtonElement | null>;
  openDownloadsPopover: (showDownloadsTab?: boolean) => void;
  closePopover: () => void;
  shouldShowDownloadsTab: boolean;
  setShouldShowDownloadsTab: (show: boolean) => void;
};

const DownloadsContext = createContext<DownloadsContextType | undefined>(undefined);

const DOWNLOADS_SEEN_KEY = 'predium_downloads_seen_state_v2';

/**
 * Fallback name for a batch export zip, used only if the presigned URL carries no
 * attachment filename — the blob download needs an explicit `download` attribute.
 */
const BATCH_EXPORT_ZIP_FILENAME_FALLBACK = 'predium_esg-analysis_batch-export.zip';

export default function DownloadsProvider({ children }: PropsWithChildren) {
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSession();
  const client = useApolloClient();

  const isCypress = typeof window !== 'undefined' && Boolean((window as any).Cypress);

  // Popover state
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const popoverAnchorEl = useRef<HTMLButtonElement>(null);
  const [shouldShowDownloadsTab, setShouldShowDownloadsTab] = useState(false);

  const initialDownloadsSeenState: DownloadsSeenState = useMemo(() => ({ pendingJobIds: [] }), []);

  const [downloadsSeenState, setDownloadsSeenState] = useLocalStorage(DOWNLOADS_SEEN_KEY, initialDownloadsSeenState);

  const updateDownloadsSeenState = useCallback(
    (state: Partial<DownloadsSeenState>) => {
      // For syncing states between tabs, it gets the obsolete state, to fix it,
      // we retrieve the recent state from the local storage directly instead of the hook
      const oldState = JSON.parse(localStorage.getItem(DOWNLOADS_SEEN_KEY) ?? '') || initialDownloadsSeenState;

      setDownloadsSeenState({
        ...oldState,
        ...state,
      });
    },
    [initialDownloadsSeenState, setDownloadsSeenState],
  );

  const pendingJobIds = useMemo(() => downloadsSeenState.pendingJobIds ?? [], [downloadsSeenState.pendingJobIds]);

  const { data: ongoingExportsData } = useSubscriptionFallback(ONGOING_EXPORTS_SUBSCRIPTION, {
    skip: isCypress || pendingJobIds.length === 0,
    variables: {
      userId: user.id,
      pendingJobIds,
    },
    skipCondition: (data) => data?.async_job.length === 0,
    useIdleOptions: {
      channelName: 'ongoing-exports-subscription',
      lastActiveTabToStayActive: true,
    },
    onData: ({ data, client }) => {
      const jobs = data?.data?.async_job ?? [];

      const doneJobs = jobs.filter((job) => job.status_id !== async_job_status_type_enum.PENDING);
      doneJobs.forEach((job) => {
        unTrackAsyncJob(job.id);

        if (job.status_id === async_job_status_type_enum.COMPLETED) {
          // The batch export zip lives in the dedicated batch-export bucket, not the
          // files bucket, so the generic exportedFileId → files-bucket path can't
          // resolve it — use the scoped presigned-URL query instead.
          if (job.title === export_jobs_enum.BATCH_ESG_ANALYSIS_PDF_EXPORT) {
            downloadBatchExport(job.id);
          } else {
            getFileIdByAsyncJobId(job.id);
          }
        }
      });

      const anyJobCompleted = jobs.some((job) => job.status_id === async_job_status_type_enum.COMPLETED);
      if (anyJobCompleted) {
        // USER_FILES_LIST covers regular exports; COMPLETED_BATCH_EXPORTS covers batch
        // zips (which aren't in the file list) so a finished batch appears in the popover.
        client.refetchQueries({ include: [USER_FILES_LIST, COMPLETED_BATCH_EXPORTS] });
      }
    },
    onError: (error) => {
      captureClientException(error, { source: 'ongoing_exports_subscription_failed' });
      enqueueSnackbar(t('Downloads_ExportsError'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    },
  });

  const ongoingExports = useMemo(
    () => (ongoingExportsData?.async_job ?? []).filter((job) => job.status_id === async_job_status_type_enum.PENDING),
    [ongoingExportsData],
  );

  const openDownloadsPopover = useCallback((showDownloadsTab: boolean = true) => {
    setShouldShowDownloadsTab(showDownloadsTab);
    setIsPopoverOpen(true);
  }, []);

  const downloadFile = useCallback(
    async (fileId: number) => {
      if (isCypress) {
        return;
      }

      try {
        const { data } = await client.query({ query: GET_FILE_DOWNLOAD_URL, variables: { fileId } });
        const file = data.file_by_pk;
        if (file) {
          downloadFileUri(file.downloadUrl, file.name ?? '');
        }
      } catch (error) {
        captureClientException(error, { source: 'download_file_failed', fileId });
        enqueueSnackbar(t('Downloads_DownloadError'), {
          variant: 'error',
          autoHideDuration: SnackbarTimeouts.Error,
        });
      }
    },
    [client, enqueueSnackbar, t, isCypress],
  );

  const getFileIdByAsyncJobId = useCallback(
    async (jobId: number) => {
      try {
        const { data } = await client.query({ query: GET_FILE_ID_BY_ASYNC_JOB_ID, variables: { asyncJobId: jobId } });
        const fileId = data.async_job_by_pk?.exportedFileId?.fileId;

        if (fileId) {
          downloadFile(fileId);
        }
      } catch (error) {
        captureClientException(error, { source: 'get_file_id_by_async_job_failed', jobId });
        enqueueSnackbar(t('Downloads_DownloadError'), {
          variant: 'error',
          autoHideDuration: SnackbarTimeouts.Error,
        });
      }
    },
    [client, downloadFile, enqueueSnackbar, t],
  );

  const downloadBatchExport = useCallback(
    async (jobId: number) => {
      if (isCypress) {
        return;
      }

      try {
        const { data } = await client.query({
          query: GET_BATCH_ESG_ANALYSIS_PDF_EXPORT_DOWNLOAD_URL,
          variables: { asyncJobId: jobId },
        });
        const url = data.getBatchEsgAnalysisPdfExportDownloadUrl;
        if (url) {
          downloadFileUri(url, getAttachmentFilenameFromUrl(url) ?? BATCH_EXPORT_ZIP_FILENAME_FALLBACK);
        }
      } catch (error) {
        captureClientException(error, { source: 'download_batch_export_failed', jobId });
        enqueueSnackbar(t('Downloads_DownloadError'), {
          variant: 'error',
          autoHideDuration: SnackbarTimeouts.Error,
        });
      }
    },
    [client, enqueueSnackbar, t, isCypress],
  );

  const trackAsyncJob = useCallback(
    (jobId: number) => {
      updateDownloadsSeenState({
        pendingJobIds: [...downloadsSeenState.pendingJobIds, jobId],
      });
    },
    [downloadsSeenState.pendingJobIds, updateDownloadsSeenState],
  );

  const unTrackAsyncJob = useCallback(
    (jobId: number) => {
      updateDownloadsSeenState({
        pendingJobIds: downloadsSeenState.pendingJobIds.filter((id) => id !== jobId),
      });
    },
    [downloadsSeenState.pendingJobIds, updateDownloadsSeenState],
  );

  const closePopover = useCallback(() => {
    setIsPopoverOpen(false);
    setShouldShowDownloadsTab(false);
  }, []);

  return (
    <DownloadsContext.Provider
      value={{
        // State
        ongoingExports,

        // Actions
        openDownloadsPopover,
        downloadFile,
        downloadBatchExport,
        trackAsyncJob,

        // Popover state
        isPopoverOpen,
        popoverAnchorEl,
        closePopover,
        shouldShowDownloadsTab,
        setShouldShowDownloadsTab,
      }}
    >
      {children}
    </DownloadsContext.Provider>
  );
}

export const useDownloads = () => {
  const context = useContext(DownloadsContext);
  if (!context) {
    throw new Error('useDownloads must be used within a DownloadsProvider');
  }
  return context;
};
